// ================================================
// CHIEF OF STAFF COMMAND SUITE
// Premium Intelligence Panel System
// ================================================
import React, { useState } from 'react';
import { 
  FileText, Calendar, CheckSquare, BookOpen, Users, 
  HandMetal, Clock, GitMerge, X
} from 'lucide-react';
import { DESIGN } from './DesignSystem';
import { CHIEF_OF_STAFF_COLORS } from './focus';
import ExecutiveBriefingPanel from './cos/ExecutiveBriefingPanel';
import MeetingPrepPanel from './cos/MeetingPrepPanel';
import ActionItemsPanel from './cos/ActionItemsPanel';
import DecisionLogPanel from './cos/DecisionLogPanel';
import StakeholderPanel from './cos/StakeholderPanel';
import DelegationPanel from './cos/DelegationPanel';
import TimeAuditPanel from './cos/TimeAuditPanel';
import SoulFoodBridgePanel from './cos/SoulFoodBridgePanel';

// Panel configuration
const PANELS = [
  { id: 'briefing', label: 'Executive Briefing', icon: FileText, description: 'Daily intelligence briefing' },
  { id: 'meetings', label: 'Meeting Prep', icon: Calendar, description: 'Meeting intelligence' },
  { id: 'actions', label: 'Action Items', icon: CheckSquare, description: 'Track commitments' },
  { id: 'decisions', label: 'Decision Log', icon: BookOpen, description: 'Organizational decisions' },
  { id: 'stakeholders', label: 'Stakeholders', icon: Users, description: 'Relationship tracker' },
  { id: 'delegations', label: 'Delegations', icon: HandMetal, description: 'Delegated tasks' },
  { id: 'timeaudit', label: 'Time Audit', icon: Clock, description: 'Executive time analysis' },
  { id: 'bridge', label: 'Soul Food Bridge', icon: GitMerge, description: 'Cross-entity view' }
];

const ChiefOfStaffCommandSuite = ({ isOpen, onClose }) => {
  const [activePanel, setActivePanel] = useState('briefing');

  const renderPanel = () => {
    switch (activePanel) {
      case 'briefing':
        return <ExecutiveBriefingPanel />;
      case 'meetings':
        return <MeetingPrepPanel />;
      case 'actions':
        return <ActionItemsPanel />;
      case 'decisions':
        return <DecisionLogPanel />;
      case 'stakeholders':
        return <StakeholderPanel />;
      case 'delegations':
        return <DelegationPanel />;
      case 'timeaudit':
        return <TimeAuditPanel />;
      case 'bridge':
        return <SoulFoodBridgePanel />;
      default:
        return <ExecutiveBriefingPanel />;
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed right-0 top-0 h-full flex z-50"
      style={{ width: '520px' }}
    >
      {/* Main Panel Content */}
      <div 
        className="flex-1 h-full overflow-hidden flex flex-col"
        style={{ 
          backgroundColor: '#0D1117',
          borderLeft: `1px solid ${CHIEF_OF_STAFF_COLORS.primary}30`
        }}
      >
        {/* Panel Header */}
        <div 
          className="flex items-center justify-between px-5 py-4 border-b"
          style={{ borderColor: CHIEF_OF_STAFF_COLORS.primary + '20' }}
        >
          <div className="flex items-center gap-3">
            <div 
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ 
                background: `linear-gradient(135deg, ${CHIEF_OF_STAFF_COLORS.primary} 0%, ${CHIEF_OF_STAFF_COLORS.primaryDark} 100%)`,
                boxShadow: `0 0 15px ${CHIEF_OF_STAFF_COLORS.primaryGlow}`
              }}
            >
              {React.createElement(PANELS.find(p => p.id === activePanel)?.icon || FileText, { 
                className: 'w-4 h-4 text-white' 
              })}
            </div>
            <div>
              <h2 className="text-white font-bold text-sm">
                {PANELS.find(p => p.id === activePanel)?.label}
              </h2>
              <p className="text-xs" style={{ color: CHIEF_OF_STAFF_COLORS.primaryLight }}>
                {PANELS.find(p => p.id === activePanel)?.description}
              </p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 rounded-lg transition-all hover:bg-white/10"
          >
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        {/* Panel Content Area */}
        <div className="flex-1 overflow-y-auto">
          {renderPanel()}
        </div>
      </div>

      {/* Icon Tab Bar */}
      <div 
        className="w-14 h-full flex flex-col items-center py-4 gap-2"
        style={{ 
          backgroundColor: '#161B22',
          borderLeft: `1px solid ${CHIEF_OF_STAFF_COLORS.primary}20`
        }}
      >
        {PANELS.map((panel) => {
          const Icon = panel.icon;
          const isActive = activePanel === panel.id;
          
          return (
            <button
              key={panel.id}
              onClick={() => setActivePanel(panel.id)}
              className="relative w-10 h-10 rounded-xl flex items-center justify-center transition-all group"
              style={{
                backgroundColor: isActive ? CHIEF_OF_STAFF_COLORS.primary : 'transparent',
                boxShadow: isActive ? `0 0 15px ${CHIEF_OF_STAFF_COLORS.primaryGlow}` : 'none'
              }}
              data-testid={`cos-panel-${panel.id}`}
            >
              <Icon 
                className="w-5 h-5 transition-all"
                style={{ 
                  color: isActive ? 'white' : CHIEF_OF_STAFF_COLORS.primaryLight 
                }}
              />
              
              {/* Tooltip */}
              <div 
                className="absolute right-full mr-3 px-3 py-1.5 rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-nowrap z-10"
                style={{ 
                  backgroundColor: '#1F2937',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                }}
              >
                <span className="text-sm font-medium text-white">
                  {panel.label}
                </span>
              </div>
            </button>
          );
        })}
        
        {/* Command Suite Label */}
        <div className="mt-auto pt-4 border-t border-white/10 w-full flex flex-col items-center gap-1">
          <div 
            className="w-6 h-6 rounded flex items-center justify-center"
            style={{ backgroundColor: CHIEF_OF_STAFF_COLORS.primary + '20' }}
          >
            <span className="text-[8px] font-black" style={{ color: CHIEF_OF_STAFF_COLORS.primary }}>
              CoS
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChiefOfStaffCommandSuite;
